// The app's own voice, in whatever language it is explaining things in.
//
// Mynah's prose is English because Mynah taught Mandarin to English speakers. The pair that makes
// that a problem is the reverse one — a Chinese speaker learning English — and for them every
// sentence the app says about ITSELF is in the wrong language. That is not a language-pair
// problem. It is that the app has one voice.
//
// THE ENGLISH IS THE KEY. There is no message id to invent, look up or keep in step: `t('Nothing
// is due.')` is both the lookup and the fallback, and a catalogue is a map from the English to
// something else. Three reasons that shape wins here over ids:
//
//   - the prose stays in the source, where it is read and edited in context. This codebase's
//     sentences were written carefully and moving them to a JSON file would gut them.
//   - a missing translation renders the English, which is a worse experience and not a broken one.
//     An id with no entry renders the id, which is a bug on screen.
//   - nothing has to be kept in step. Editing the English orphans its translation, and the
//     consequence is the English — see above.
//
// The cost is that identical English in two places cannot say two different things. Nothing in
// Mynah needs that today; when something does, it wants a different sentence anyway.

/**
 * The language the app's own strings are written in.
 *
 * Not a setting. Every `t('…')` in this codebase is English, so English is the one language that
 * needs no catalogue anywhere — as the app's voice it falls back, and as the language being
 * learned it is already the phrase.
 */
const SOURCE = 'en';

let messages: Record<string, string> = {};
let target: Record<string, string> = {};
let targetIsSource = false;

/** Hand over the catalogue for the language the app explains things in. English passes none. */
export function speakIn(catalogue: Record<string, string> | null | undefined): void {
  messages = catalogue ?? {};
}

/**
 * Hand over the catalogue for the language being LEARNED.
 *
 * Used in the two places the app deliberately shows a taste of it rather than explaining in it:
 * the greeting's second line, and the second line on every tile. 词语 under "Words" is not a
 * translation for the reader's benefit — it is the thing they came here for, and it belongs to
 * the language on the cards, not to the language of the app.
 */
export function speakInTarget(catalogue: Record<string, string> | null | undefined): void {
  target = catalogue ?? {};
}

/**
 * Which language is being learned.
 *
 * Set separately from the catalogue because the two are known at different moments: the
 * catalogues are fetched before anything is drawn, so that even a sign-in screen speaks, while
 * which language is being LEARNED is not known until the pair has loaded. Until it is, the
 * default pair's answer stands, and the only thing that depends on it is a tile's second line.
 */
export function learningIs(code: string): void {
  targetIsSource = code.split('-')[0] === SOURCE;
}

/**
 * The same phrase, in the language being learned.
 *
 * Returns null rather than the English when there is nothing: a tile with its second line missing
 * is a tile, and a tile whose second line repeats the first is a mistake on screen.
 *
 * AND IT CHECKS THAT SECOND CLAUSE RATHER THAN ASSUMING IT. The app's own voice is chosen at
 * runtime now, so it can land on the very language being learned — a Chinese speaker who switches
 * the app to Chinese while studying Chinese — and every tile then said 词语 twice, once as its
 * heading and once as its taste of the language. Comparing against what the app just said is the
 * only form of this rule that does not depend on which languages happen to be involved.
 */
export function inTarget(text: string): string | null {
  const other = targetIsSource ? text : (target[text] ?? null);
  return other === null || other === t(text) ? null : other;
}

/** How many phrases this voice actually has. For the honesty of a coverage test. */
export function phraseCount(): number {
  return Object.keys(messages).length;
}

/**
 * Say something, in the app's current voice.
 *
 * Two shapes, because the strings come in two shapes:
 *
 *     t('Nothing is due.')
 *     t`${done} of ${total} words`
 *
 * The tagged form keys on the PATTERN with the values punched out — "{0} of {1} words" — so a
 * catalogue holds one entry however many numbers pass through it, and a translator can move the
 * pieces around, which some languages require and English never notices.
 */
export function t(text: string): string;
export function t(strings: TemplateStringsArray, ...values: unknown[]): string;
export function t(first: string | TemplateStringsArray, ...values: unknown[]): string {
  if (typeof first === 'string') return messages[first] ?? first;

  const pattern = first.reduce((acc, part, i) => (i === 0 ? part : `${acc}{${i - 1}}${part}`), '');
  const said = messages[pattern] ?? pattern;
  return said.replace(/\{(\d+)\}/gu, (whole, n: string) => {
    const value = values[Number(n)];
    return value === undefined ? whole : String(value);
  });
}
